import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthGuardData, createAuthGuard } from 'keycloak-angular';
import { inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import { PermissionService } from '../services/permission.service';

/**
 * Auth guard with optional capability check.
 *
 * Behaviour:
 *  - If the user is unauthenticated → trigger Keycloak login.
 *  - If `route.data.requiredKeys` is set → user must have ALL listed keys
 *    (logical AND). Missing → return false (router stays at the previous URL).
 *  - If `route.data.requiredAnyKeys` is set → user must have AT LEAST ONE
 *    listed key (logical OR).
 *  - If neither is set → authentication alone is sufficient.
 *
 * Example route:
 *   {
 *     path: 'pos',
 *     canActivate: [canActivateAuthRole],
 *     data: { requiredKeys: ['pos:access'] },   // enforce after Keycloak roles configured
 *     loadComponent: () => import(...).then(m => m.PosPage),
 *   }
 *
 * NOTE: As of 2026-05-07 the Keycloak `business` realm does not yet expose
 * granular POS roles (see `pos-bugs-v1.md → POS-RISK-1`). Until those roles
 * are provisioned, do NOT add `requiredKeys` to the `/pos` route — every
 * existing user would be locked out. The framework here makes enabling them
 * a one-line config change once the realm is updated.
 */
const isAccessAllowed = async (
    route: ActivatedRouteSnapshot,
    _: RouterStateSnapshot,
    authData: AuthGuardData,
): Promise<boolean | UrlTree> => {
    const { authenticated } = authData;

    if (!authenticated) {
        const keycloak = inject(Keycloak);
        keycloak.login();
        return false;
    }

    const requiredKeys = route.data?.['requiredKeys'] as string[] | undefined;
    const requiredAnyKeys = route.data?.['requiredAnyKeys'] as string[] | undefined;

    if (!requiredKeys?.length && !requiredAnyKeys?.length) {
        return true;
    }

    const perms = inject(PermissionService);

    if (requiredKeys?.length && !perms.hasAll(requiredKeys)) {
        return false;
    }

    if (requiredAnyKeys?.length && !perms.hasAny(requiredAnyKeys)) {
        return false;
    }

    return true;
};

export const canActivateAuthRole = createAuthGuard<CanActivateFn>(isAccessAllowed);
